import React, { useState, useRef, useMemo } from "react";
import { Spinner } from "@chakra-ui/react";
import { useNavigate } from "@reach/router";
import {
  FiPlus,
  FiSend,
  FiDownload,
  FiUpload,
  FiFileText,
  FiX,
} from "react-icons/fi";
import { Button } from "newui/components/Button";
import { TableColumnFilter } from "newui/components/Filters";
import { useClarifications } from "../../hooks/useClarifications";
import { ClarificationRow } from "../ClarificationRow";
import { ClarificationModal } from "../ClarificationModal";
import { ClarificationEmptyState } from "../ClarificationEmptyState";
import { ImportStatusModal } from "../ImportStatusModal";
import { DeleteConfirmationModal } from "../DeleteConfirmationModal";
import { SendConfirmationModal } from "../SendConfirmationModal";
import { SendResponsesModal } from "../SendResponsesModal";
import { ImportValidationModal } from "../ImportValidationModal";
import {
  CLARIFICATION_STATUS_LABELS,
  CLARIFICATION_IMPACT_LABELS,
} from "../../constants/clarification.constants";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { RFX_TABS } from "utils/constants";
import type {
  Clarification,
  ClarificationStatus,
  ClarificationImpact,
  CreateClarificationPayload,
  UpdateClarificationPayload,
  ImportClarificationsResult,
  ValidateImportResult,
  SubmitClarificationResponsePayload,
} from "../../types/clarification.type";
import { useHasClarificationsAccess } from "hooks/featureFlags";
import { FileableFile } from "types/storage.types";

interface ClarificationsRegisterProps {
  isClientView: boolean;
  rfxId?: number;
  rfxSubmissionId?: number;
  supplierFilter?: number;
  supplierName: string;
}

export const ClarificationsRegister: React.FC<ClarificationsRegisterProps> = ({
  rfxId,
  rfxSubmissionId,
  supplierName,
  isClientView,
  supplierFilter,
}) => {
  const navigate = useNavigate();
  const { clientPaths } = useAuthContext();

  const {
    clarifications,
    clarificationTypes,
    draftClarifications,
    clarificationsWithDraftResponses,
    isLoading,
    addClarification,
    editClarification,
    removeClarification,
    sendDraftClarifications,
    handleExport,
    handleDownloadTemplate,
    handleImport,
    handleValidateImport,
    saveDraftResponse,
    sendSelectedResponses,
    updateLocalClarifications,
  } = useClarifications({ rfxId, rfxSubmissionId, isClientView });

  const isClarificationsEnabled = useHasClarificationsAccess();

  const selectableItems = isClientView ? draftClarifications : clarificationsWithDraftResponses;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClarification, setSelectedClarification] = useState<Clarification | null>(
    null
  );
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isSendingResponses, setIsSendingResponses] = useState(false);
  const [importResult, setImportResult] = useState<ImportClarificationsResult | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [deletingClarification, setDeletingClarification] = useState<Clarification | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [isSendResponsesModalOpen, setIsSendResponsesModalOpen] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidateImportResult | null>(null);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filter state
  const [supplierFilters, setSupplierFilters] = useState<number[]>([]);
  const [statusFilters, setStatusFilters] = useState<ClarificationStatus[]>([]);
  const [impactFilters, setImpactFilters] = useState<(ClarificationImpact | "not_set")[]>([]);

  const handleOpenCreateModal = () => {
    setSelectedClarification(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (clarification: Clarification) => {
    setSelectedClarification(clarification);
    setIsModalOpen(true);
  };

  const handleOpenViewModal = (clarification: Clarification) => {
    setSelectedClarification(clarification);
    setIsModalOpen(true);
  };

  const handleCloseModal = (uploadFiles: FileableFile[]) => {
    setIsModalOpen(false);
    setSelectedClarification(null);

    if (uploadFiles && selectedClarification) {
      updateLocalClarifications(clarifications.map((c) => c.id === selectedClarification.id ? { ...c, files: uploadFiles } : c));
    }
  };

  const handleSave = async (
    payload: CreateClarificationPayload | UpdateClarificationPayload,
    clarificationId?: number
  ): Promise<boolean> => {
    if (clarificationId) {
      return editClarification(clarificationId, payload as UpdateClarificationPayload);
    }
    return addClarification(payload as CreateClarificationPayload);
  };

  const handleResponseSave = async (
    payload: SubmitClarificationResponsePayload,
    clarificationId?: number
  ): Promise<boolean> => {
    if (!clarificationId) {
      return false;
    }
    // Save as draft instead of immediate send
    return saveDraftResponse(clarificationId, payload);
  };

  const handleDelete = async (clarification: Clarification) => {
    if (!clarification.is_sent) {
      setDeletingClarification(clarification);
      setIsDeleteModalOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingClarification) {
      return;
    }
    setIsDeleting(true);
    try {
      await removeClarification(deletingClarification.id);
      setIsDeleteModalOpen(false);
      setDeletingClarification(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleSelection = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === selectableItems.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableItems.map((c) => c.id));
    }
  };

  const handleSendSelected = async () => {
    if (selectedIds.length === 0) {
      return;
    }

    if (isClientView) {
      setIsSendModalOpen(true);
    } else {
      setIsSendResponsesModalOpen(true);
    }
  };

  const handleConfirmSend = async (dateDue?: string) => {
    setIsSending(true);
    try {
      const success = isClientView ? await sendDraftClarifications(selectedIds, dateDue) : await sendSelectedResponses(selectedIds);
      if (success) {
        setSelectedIds([]);
        if (isClientView) {
          setIsSendModalOpen(false);
        } else {
          setIsSendResponsesModalOpen(false);
        }
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const result = await handleValidateImport(file);
      if (result) {
        setValidationResult(result);
        setIsValidationModalOpen(true);
      }
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleConfirmImport = async (rows: any) => {
    const result = await handleImport(rows);
    if (result && result.errors.length === 0) {
      setIsValidationModalOpen(false);
      setValidationResult(null);
    }
    return result;
  };

  // Extract unique suppliers from clarifications
  const suppliers = useMemo(() => {
    const uniqueSuppliers = new Map<number, { id: number; name: string }>();
    clarifications.forEach((c) => {
      if (c.rfx_submission_id && c.supplier_name) {
        uniqueSuppliers.set(c.rfx_submission_id, {
          id: c.rfx_submission_id,
          name: c.supplier_name,
        });
      }
    });
    return Array.from(uniqueSuppliers.values());
  }, [clarifications]);

  // Extract unique impact values that exist in the data
  const availableImpacts = useMemo(() => {
    const uniqueImpacts = new Set<ClarificationImpact | "not_set">();
    let hasNotSet = false;

    clarifications.forEach((c) => {
      if (c.clarification_impact_id) {
        uniqueImpacts.add(c.clarification_impact_id);
      } else {
        hasNotSet = true;
      }
    });

    const impacts: Array<{ label: string; value: ClarificationImpact | "not_set"; }> = [];

    // Add existing impacts
    Array.from(uniqueImpacts).forEach((impact) => {
      impacts.push({
        value: impact,
        label: CLARIFICATION_IMPACT_LABELS[impact as ClarificationImpact],
      });
    });

    // Add "Not Set" option if there are clarifications without impact
    if (hasNotSet) {
      impacts.push({
        value: "not_set",
        label: "Not Set",
      });
    }

    return impacts;
  }, [clarifications]);

  // Extract unique status values that exist in the data
  const availableStatuses = useMemo(() => {
    const uniqueStatuses = new Set<ClarificationStatus>();
    clarifications.forEach((c) => {
      if (c.status) {
        uniqueStatuses.add(c.status);
      }
    });

    const statuses = Array.from(uniqueStatuses)
      .map((status) => {
        const label = CLARIFICATION_STATUS_LABELS[status];
        return {
          value: status,
          label: label || 'Unknown',
        };
      })
      .filter((item) => item.label); // Remove any with undefined labels

    // Fallback: if no statuses found in data, show all possible statuses
    if (statuses.length === 0) {
      return Object.entries(CLARIFICATION_STATUS_LABELS).map(([value, label]) => ({
        value: Number(value) as ClarificationStatus,
        label,
      }));
    }

    return statuses;
  }, [clarifications]);

  // Apply filters
  const filteredClarifications = useMemo(() => {
    let filtered = supplierFilter
      ? clarifications.filter((c) => c.rfx_submission_id === supplierFilter)
      : clarifications;

    // Apply supplier filter
    if (supplierFilters.length > 0) {
      filtered = filtered.filter((c) =>
        supplierFilters.includes(c.rfx_submission_id)
      );
    }

    // Apply status filter
    if (statusFilters.length > 0) {
      filtered = filtered.filter((c) => statusFilters.includes(c.status));
    }

    // Apply impact filter
    if (impactFilters.length > 0) {
      filtered = filtered.filter((c) => {
        // Check if "not_set" is selected and clarification has no impact
        if (impactFilters.includes("not_set") && !c.clarification_impact_id) {
          return true;
        }
        // Check if clarification's impact matches any selected filter
        return c.clarification_impact_id && impactFilters.includes(c.clarification_impact_id);
      });
    }

    return filtered;
  }, [
    clarifications,
    supplierFilter,
    supplierFilters,
    statusFilters,
    impactFilters,
  ]);

  // For suppliers, only show sent clarifications
  const displayClarifications = isClientView
    ? filteredClarifications
    : filteredClarifications.filter((c) => c.is_sent);

  const supplierNameForSend = selectableItems[0]?.supplier_name || "supplier";

  // Get selected clarifications for send responses modal
  const selectedItemsForSending = selectableItems.filter((c) =>
    selectedIds.includes(c.id)
  );

  // Clear all filters
  const handleClearAllFilters = () => {
    setSupplierFilters([]);
    setStatusFilters([]);
    setImpactFilters([]);
  };

  // Remove individual supplier filter
  const handleRemoveSupplierFilter = (supplierId: number) => {
    setSupplierFilters((prev) => prev.filter((id) => id !== supplierId));
  };

  // Remove individual status filter
  const handleRemoveStatusFilter = (status: ClarificationStatus) => {
    setStatusFilters((prev) => prev.filter((s) => s !== status));
  };

  // Remove individual impact filter
  const handleRemoveImpactFilter = (impact: ClarificationImpact | "not_set") => {
    setImpactFilters((prev) => prev.filter((i) => i !== impact));
  };

  const handleSupplierNameClick = (submissionId: number) => {
    navigate(clientPaths?.viewRfxSubmission(rfxId, submissionId, RFX_TABS.CLARIFICATIONS));
  }

  const handleRowClick = (clarification: Clarification) => {

    if (isClientView) {
      return clarification.is_sent ? handleOpenViewModal(clarification) : handleOpenEditModal(clarification);
    }

    return !!!clarification.final_response ? handleOpenViewModal(clarification) : handleOpenEditModal(clarification);
  };

  const activeFilterCount =
    supplierFilters.length + statusFilters.length + impactFilters.length;

  // Get active filter details for display with individual removal
  const activeFilterDetails = useMemo(() => {
    const details: Array<{
      label: string;
      type: "supplier" | "status" | "impact";
      value: any;
    }> = [];

    supplierFilters.forEach((id) => {
      const supplier = suppliers.find((s) => s.id === id);
      if (supplier) {
        details.push({
          type: "supplier",
          label: `Supplier: ${supplier.name}`,
          value: id,
        });
      }
    });

    statusFilters.forEach((status) => {
      const statusLabel = CLARIFICATION_STATUS_LABELS[status] || status;
      details.push({
        type: "status",
        label: `Status: ${statusLabel}`,
        value: status,
      });
    });

    impactFilters.forEach((impact) => {
      const impactLabel = impact === "not_set" ? "Not Set" : CLARIFICATION_IMPACT_LABELS[impact];
      details.push({
        type: "impact",
        label: `Impact: ${impactLabel}`,
        value: impact,
      });
    });

    return details;
  }, [supplierFilters, statusFilters, impactFilters, suppliers]);

  if (!isClarificationsEnabled) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" color="blue.500" />
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 px-4 py-4">
        <div className="flex items-center gap-4 flex-1">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Clarifications Register</h3>
            <p className="text-sm text-gray-500">
              {isClientView
                ? "Manage clarification requests sent to suppliers"
                : "View and respond to clarification requests"}
            </p>
          </div>
          {activeFilterCount > 0 && (
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                onClick={handleClearAllFilters}
                btntype="base"
                className="flex items-center gap-1 !py-1 !px-2 !text-sm"
                title="Clear all filters"
              >
                <FiX className="h-3 w-3" />
                Clear All
              </Button>
              <div className="flex flex-wrap gap-2">
                {activeFilterDetails.map((detail, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      if (detail.type === "supplier") {
                        handleRemoveSupplierFilter(detail.value);
                      } else if (detail.type === "status") {
                        handleRemoveStatusFilter(detail.value);
                      } else if (detail.type === "impact") {
                        handleRemoveImpactFilter(detail.value);
                      }
                    }}
                    className="flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200 transition-colors"
                    title={`Remove ${detail.label} filter`}
                  >
                    <span>{detail.label}</span>
                    <FiX className="h-3 w-3" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        {rfxSubmissionId && (
          <div className="flex flex-wrap items-center gap-2">
            {/* Export/Import buttons */}
            {clarifications?.length > 0 && <Button
              onClick={handleExport}
              btntype="base"
              className="flex items-center gap-2"
              title="Export clarifications"
            >
              <FiDownload className="h-4 w-4" />
              Export
            </Button>}
            <Button
              onClick={handleDownloadTemplate}
              btntype="base"
              className="flex items-center gap-2"
              title="Download import template"
            >
              <FiFileText className="h-4 w-4" />
              Template
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              btntype="base"
              className="flex items-center gap-2"
              title="Import clarifications"
            >
              <FiUpload className="h-4 w-4" />
              Import
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            {/* Add button */}
            {isClientView && <Button
              onClick={handleOpenCreateModal}
              className="flex items-center gap-2"
              data-testid="add-clarification-button"
            >
              <FiPlus className="h-4 w-4" />
              Add Clarification
            </Button>}
          </div>
        )}
      </div>

      {/* Selectable Items */}
      {rfxSubmissionId && selectableItems.length > 0 && (
        <div className="border-b border-gray-200 bg-blue-50 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedIds.length === selectableItems.length}
                  onChange={handleSelectAll}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm font-medium text-blue-700">
                  {selectedIds.length} of {selectableItems.length} draft {isClientView ? 'clarifications' : 'responses'} selected
                </span>
              </label>
            </div>
            <Button
              onClick={handleSendSelected}
              disabled={selectedIds.length === 0 || isSending}
              className="flex items-center gap-2"
              data-testid="send-clarifications-button"
            >
              {isSending ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <FiSend className="h-4 w-4" />
                  Send Selected
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {displayClarifications.length === 0 ? (
        <ClarificationEmptyState isClientView={isClientView} />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {rfxSubmissionId && selectableItems.length > 0 && (
                  <th className="w-10 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {/* Checkbox column for drafts */}
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">
                  Type
                </th>
                <th className="w-[300px] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Clarification
                </th>
                <th className="w-[200px] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Relates To
                </th>
                {isClientView && !!!rfxSubmissionId && suppliers.length > 0 && (
                  <th className="border-b border-neutral-300 px-4 py-2">
                    <TableColumnFilter
                      label="SUPPLIER"
                      className="py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      options={suppliers.map((s) => ({
                        value: s.id,
                        label: s.name,
                      }))}
                      value={supplierFilters}
                      onChange={setSupplierFilters}
                      isMulti
                    />
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">
                  Owner
                </th>
                {/* <th className="border-b border-neutral-300 px-4 py-2 whitespace-nowrap">
                  <TableColumnFilter
                    label="IMPACT"
                    className="py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    options={availableImpacts}
                    value={impactFilters}
                    onChange={setImpactFilters}
                    isMulti
                  />
                </th> */}
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">
                  Date Sent
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">
                  Deadline
                </th>
                <th className="w-[300px] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Response
                </th>
                <th className="border-b border-neutral-300 px-4 py-2 text-center whitespace-nowrap">
                  <TableColumnFilter
                    label="STATUS"
                    className="py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    options={availableStatuses}
                    value={statusFilters}
                    onChange={setStatusFilters}
                    isMulti
                  />
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {displayClarifications.map((clarification) => (
                <ClarificationRow
                  key={clarification.id}
                  clarification={clarification}
                  hasSelectableItems={rfxSubmissionId && selectableItems?.length > 0}
                  isClientView={isClientView}
                  isSelected={selectedIds.includes(clarification.id)
                  }
                  showSupplierColumn={isClientView && suppliers.length > 0 && !!!rfxSubmissionId}
                  onRowClick={() => handleRowClick(clarification)}
                  onToggleSelect={() => handleToggleSelection(clarification.id)}
                  onEdit={handleOpenEditModal}
                  onView={handleOpenViewModal}
                  onDelete={handleDelete}
                  onInlineEdit={isClientView ? editClarification : undefined}
                  onSupplierNameClick={handleSupplierNameClick}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ClarificationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        clarification={selectedClarification}
        clarificationTypes={clarificationTypes}
        isClientView={isClientView}
        supplierName={supplierName}
        onClientSave={handleSave}
        onSupplierSave={handleResponseSave}
      />

      <ImportStatusModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        result={importResult}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeletingClarification(null);
        }}
        onConfirm={handleConfirmDelete}
        clarification={deletingClarification}
        isLoading={isDeleting}
      />

      <SendConfirmationModal
        isOpen={isSendModalOpen}
        onClose={() => setIsSendModalOpen(false)}
        onConfirm={handleConfirmSend}
        clarifications={selectedItemsForSending}
        supplierName={supplierNameForSend}
        isLoading={isSending}
      />

      <SendResponsesModal
        isOpen={isSendResponsesModalOpen}
        onClose={() => setIsSendResponsesModalOpen(false)}
        onConfirm={handleConfirmSend}
        clarifications={selectedItemsForSending}
        isLoading={isSendingResponses}
      />

      {validationResult && (
        <ImportValidationModal
          isOpen={isValidationModalOpen}
          onClose={() => {
            setIsValidationModalOpen(false);
            setValidationResult(null);
          }}
          onImport={handleConfirmImport}
          validationResult={validationResult}
          clarificationTypes={clarificationTypes}
          rfxSubmissionId={rfxSubmissionId || 0}
        />
      )}
    </div>
  );
};
